/**
 * Feature Flags System
 *
 * Manages feature flags for gradual rollout of new features.
 * Used for safe deployment with progressive rollout (10% → 50% → 100%).
 */

// ============================================================================
// FEATURE FLAG DEFINITIONS
// ============================================================================

export type FeatureFlag =
  | 'unified-scorecard'
  | 'enhanced-analytics'
  | 'social-sharing-v2'
  | 'coach-sessions-consolidation'
  | 'reports-revenue-by-source'
  | 'reports-weather-impact'
  | 'revenue-pro-shop'
  | 'revenue-green-fees'
  | 'revenue-memberships'
  // Add more feature flags as needed
  ;

export interface FeatureFlagConfig {
  enabled: boolean;
  rolloutPercentage: number; // 0-100
  overrides?: {
    userIds?: string[]; // Specific users who get the feature
    clubIds?: string[]; // Specific clubs who get the feature
  };
}

// ============================================================================
// FEATURE FLAG CONFIGURATION
// ============================================================================

/**
 * Central feature flag configuration
 *
 * For gradual rollout:
 * - Week 1: 10% (rolloutPercentage: 10)
 * - Week 2: 50% (rolloutPercentage: 50)
 * - Week 3+: 100% (rolloutPercentage: 100)
 */
const FEATURE_FLAGS: Record<FeatureFlag, FeatureFlagConfig> = {
  'unified-scorecard': {
    enabled: true,
    rolloutPercentage: 0, // Start at 0% (disabled for everyone except overrides)
    overrides: {
      userIds: [], // Add specific user IDs for testing
      clubIds: [], // Add specific club IDs for testing
    },
  },
  'enhanced-analytics': {
    enabled: false,
    rolloutPercentage: 0,
  },
  'social-sharing-v2': {
    enabled: false,
    rolloutPercentage: 0,
  },
  // GM Command Center: Revenue by Source section (hidden until services re-launch).
  'reports-revenue-by-source': {
    enabled: true,
    rolloutPercentage: 0,
    overrides: {
      userIds: ['83f0046e-269c-4863-9f68-1fc753996bc4'],
      clubIds: ['31e4b90f-d37f-4148-9542-b67f35251877'],
    },
  },
  'reports-weather-impact': {
    enabled: false,
    rolloutPercentage: 0,
    overrides: {
      userIds: [],
      clubIds: [],
    },
  },
  // Pro Shop revenue row — hidden during brand pivot; re-enable when POS integration ships.
  'revenue-pro-shop': {
    enabled: false,
    rolloutPercentage: 0,
    overrides: {
      userIds: [],
      clubIds: [],
    },
  },
  // Green Fees revenue row — hidden during brand pivot; re-enable when tee sheet feature ships.
  'revenue-green-fees': {
    enabled: false,
    rolloutPercentage: 0,
    overrides: {
      userIds: [],
      clubIds: [],
    },
  },
  // Memberships revenue row — weekly view not meaningful for annual dues; tracked on /club-admin/reports/revenue instead.
  'revenue-memberships': {
    enabled: false,
    rolloutPercentage: 0,
    overrides: {
      userIds: [],
      clubIds: [],
    },
  },
  // Coach portal consolidation (see design doc 2026-05-24).
  // Gates: new /coach-portal/library route, dashboard tab cleanup, student
  // profile Lessons+Plans split, QuickSendSheet, lesson-link picker in
  // SessionBuilder, inline homework block on lesson detail.
  // Roll out: 0 (dev only, via override) → 100 (after PR1 migration lands).
  'coach-sessions-consolidation': {
    enabled: true,
    rolloutPercentage: 100, // dev-on; flip to 0 to hide in prod until migration ships
    overrides: {
      userIds: [],
      clubIds: [],
    },
  },
};

// ============================================================================
// FEATURE FLAG FUNCTIONS
// ============================================================================

/**
 * Check if a feature is enabled for a specific user
 *
 * @param flag - Feature flag to check
 * @param userId - User ID to check against
 * @param clubId - Optional club ID to check against
 * @returns true if feature is enabled for this user
 */
export function isFeatureEnabled(
  flag: FeatureFlag,
  userId?: string,
  clubId?: string
): boolean {
  const config = FEATURE_FLAGS[flag];

  // Feature is globally disabled
  if (!config.enabled) {
    return false;
  }

  // Check user-specific override
  if (userId && config.overrides?.userIds?.includes(userId)) {
    return true;
  }

  // Check club-specific override
  if (clubId && config.overrides?.clubIds?.includes(clubId)) {
    return true;
  }

  // Check rollout percentage
  if (config.rolloutPercentage === 100) {
    return true; // Fully rolled out
  }

  if (config.rolloutPercentage === 0) {
    return false; // Not rolled out yet
  }

  // Deterministic percentage-based rollout
  // Uses user ID to ensure consistent experience
  if (userId) {
    const hash = simpleHash(userId);
    const percentage = hash % 100;
    return percentage < config.rolloutPercentage;
  }

  return false;
}

/**
 * Get all enabled features for a user
 */
export function getEnabledFeatures(userId?: string, clubId?: string): FeatureFlag[] {
  const enabledFlags: FeatureFlag[] = [];

  for (const flag of Object.keys(FEATURE_FLAGS) as FeatureFlag[]) {
    if (isFeatureEnabled(flag, userId, clubId)) {
      enabledFlags.push(flag);
    }
  }

  return enabledFlags;
}

/**
 * Update feature flag configuration (for admin/testing)
 * NOTE: In production, this should be moved to a backend API
 */
export function updateFeatureFlag(
  flag: FeatureFlag,
  config: Partial<FeatureFlagConfig>
): void {
  FEATURE_FLAGS[flag] = {
    ...FEATURE_FLAGS[flag],
    ...config,
  };

  console.log(`[FeatureFlags] Updated ${flag}:`, FEATURE_FLAGS[flag]);
}

/**
 * Get current feature flag configuration (for debugging)
 */
export function getFeatureFlagConfig(flag: FeatureFlag): FeatureFlagConfig {
  return { ...FEATURE_FLAGS[flag] };
}

/**
 * Get all feature flags (for admin dashboard)
 */
export function getAllFeatureFlags(): Record<FeatureFlag, FeatureFlagConfig> {
  return { ...FEATURE_FLAGS };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Simple hash function for consistent user-based rollout
 * Ensures same user always gets same result
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// ============================================================================
// REACT HOOK
// ============================================================================

import { useMemo } from 'react';

/**
 * React hook for feature flags
 *
 * Usage:
 * ```typescript
 * const { isEnabled, allEnabledFeatures } = useFeatureFlags(user?.id, club?.id);
 *
 * if (isEnabled('unified-scorecard')) {
 *   return <UnifiedScoreEntry />;
 * } else {
 *   return <SoloScoreEntry />;
 * }
 * ```
 */
export function useFeatureFlags(userId?: string, clubId?: string) {
  const isEnabled = useMemo(() => {
    return (flag: FeatureFlag) => isFeatureEnabled(flag, userId, clubId);
  }, [userId, clubId]);

  const allEnabledFeatures = useMemo(() => {
    return getEnabledFeatures(userId, clubId);
  }, [userId, clubId]);

  return {
    isEnabled,
    allEnabledFeatures,
  };
}
